/**
 * Lógica pura de los lotes de pago (sin React), para poder testearla.
 * [NUEVO 04/11/2026]
 *
 * Los flags BIT que el backend expone por vistas pueden llegar como 0/1 en vez
 * de booleanos (ver flagsNumericos.test.ts): acá siempre se pasan por
 * Boolean(...) antes de usarlos.
 */
import type {
  AprobacionLote,
  AprobacionLoteDetalle,
  EstadoLotePago,
  EventoLote,
  FacturaDeLote,
  FacturaParaPagar,
  LoteBanco,
  LotePago,
  LotePagoDetalle,
  ResultadoConciliacion,
} from "../types/reviewItem";
import { formatFecha, formatMonto, totalesPorMoneda } from "./formato";

/** Aprobaciones necesarias de usuarios distintos (mismo número que el backend). */
export const APROBACIONES_NECESARIAS = 2;

export const ETIQUETA_ESTADO_LOTE: Record<EstadoLotePago, string> = {
  ABIERTO: "Abierto",
  CONCILIADO: "Conciliado",
  PARA_CORREGIR: "Para corregir",
  APROBADO: "Aprobado",
  PAGADO: "Pagado",
  PAGADO_SIN_APROBACION: "Pagado sin aprobación",
};

/** Reusa las clases de badge que ya existen en global.css. */
export const CLASE_ESTADO_LOTE: Record<EstadoLotePago, string> = {
  ABIERTO: "badge-wait",
  CONCILIADO: "badge-pend",
  PARA_CORREGIR: "badge-rej",
  APROBADO: "badge-auth",
  PAGADO: "badge-auth",
  PAGADO_SIN_APROBACION: "badge-rej",
};

export const ETIQUETA_RESULTADO: Record<ResultadoConciliacion, string> = {
  OK: "Cuadra",
  DIFERENCIA: "Con diferencia",
  SOLO_EN_APP: "Solo en la app",
  SOLO_EN_BANCO: "Solo en el banco",
  SIN_PROVEEDOR: "Proveedor sin identificar",
  NO_HABILITADA: "Factura no habilitada",
};

/** Solo un lote ABIERTO o PARA_CORREGIR admite agregar, quitar o mover facturas. */
export function esEditable(estado: EstadoLotePago): boolean {
  return estado === "ABIERTO" || estado === "PARA_CORREGIR";
}

/**
 * Refleja `validar_seleccion` del backend en una línea, para avisar sin ida y
 * vuelta. El backend sigue siendo la barrera real. `null` = se puede agrupar.
 */
export function motivoNoSePuedeAgrupar(sel: FacturaParaPagar[], monedaLote?: string): string | null {
  if (sel.length === 0) return "Elegí al menos una factura";
  if (sel.some((f) => !f.moneda || f.saldo_vencido === null || f.saldo_vencido === undefined)) {
    return "Alguna factura no tiene moneda o saldo; recargá la bandeja";
  }
  const monedas = new Set(sel.map((f) => f.moneda));
  if (monedas.size > 1) return "No se pueden mezclar monedas en un mismo lote";
  if (monedaLote && sel[0].moneda !== monedaLote) return `El lote es en ${monedaLote}: elegí facturas de esa moneda`;
  if (sel.some((f) => !Boolean(f.pago_habilitado))) return "Todas las facturas tienen que estar habilitadas para pagar";
  const enLote = sel.find((f) => f.lote_id !== null && f.lote_id !== undefined);
  if (enLote) return `Una factura ya está en ${enLote.lote_nombre ?? "otro lote"}`;
  return null;
}

/** Lotes a los que se pueden mover facturas: editables, de la misma moneda y no el propio. */
export function lotesDestino(lotes: LotePago[], moneda: string, excluirId?: number): LotePago[] {
  return lotes.filter((l) => esEditable(l.estado) && l.moneda === moneda && l.id !== excluirId);
}

function plural(n: number, singular: string, pluralTxt: string): string {
  return `${n} ${n === 1 ? singular : pluralTxt}`;
}

/**
 * Línea de resumen de la conciliación; `noCierran` = proveedores que no cuadran.
 * [AMPLIADO Tarea 9] `noCierran` es opcional: el listado no trae la
 * conciliación por proveedor, solo el detalle.
 */
export function resumenConciliacion(
  l: Pick<LotePago, "estado" | "cierra" | "total" | "moneda" | "cantidad_proveedores">,
  noCierran?: number
): string {
  if (l.cierra === null || l.cierra === undefined) return "Sin conciliar";
  if (Boolean(l.cierra)) {
    return `Cierra con el banco · ${plural(l.cantidad_proveedores, "proveedor", "proveedores")} · ${l.moneda} ${formatMonto(l.total)}`;
  }
  if (noCierran === undefined) return "No cierra con el banco";
  return `No cierra: ${plural(noCierran, "proveedor", "proveedores")} sin cuadrar con el banco`;
}

/**
 * El backend manda fechas UTC sin zona ("2026-11-04 13:32:00.123456") o ISO con
 * zona. Se muestran como "DD/MM HH:MM" en hora de Montevideo.
 */
export function diaHoraMontevideo(valor: string): string {
  let iso = valor.trim().replace(" ", "T").replace(/(\.\d{3})\d+/, "$1");
  if (!/(Z|[+-]\d{2}:?\d{2})$/.test(iso)) iso += "Z";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const partes = new Intl.DateTimeFormat("es-UY", {
    timeZone: "America/Montevideo",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(d);
  // padStart: algunos ICU ignoran "2-digit" con es-UY ("4/11").
  const p = (t: string) => (partes.find((x) => x.type === t)?.value ?? "").padStart(2, "0");
  return `${p("day")}/${p("month")} ${p("hour")}:${p("minute")}`;
}

/**
 * "1 de 2 · ana.perez 04/11 10:32". El backend devuelve el UPN, no el nombre
 * visible: se muestra la parte local (antes del @), que alcanza para
 * reconocer a la persona sin una consulta extra a Graph por cada lote.
 */
export function textoAprobaciones(aprobaciones: AprobacionLote[]): string {
  const base = `${aprobaciones.length} de ${APROBACIONES_NECESARIAS}`;
  if (aprobaciones.length === 0) return base;
  const detalle = aprobaciones.map((a) => `${a.usuario.split("@")[0]} ${diaHoraMontevideo(a.fecha)}`);
  return `${base} · ${detalle.join(" · ")}`;
}

/** Los nombres automáticos son "Lote N · $ · DD/MM": en la confirmación alcanza con "Lote N". */
const NOMBRE_AUTO_RE = /^(Lote \d+) · .+ · \d{2}\/\d{2}$/;

/** "Lote 3 · $ · 04/11" → "Lote 3"; un nombre que no es automático queda igual. */
export function nombreCorto(nombre: string): string {
  return NOMBRE_AUTO_RE.exec(nombre)?.[1] ?? nombre;
}

export function textoConfirmarAprobar(l: LotePago): string {
  const nombre = nombreCorto(l.nombre);
  return `Aprobar ${nombre} · ${l.moneda} ${formatMonto(l.total)} · ${plural(l.cantidad_proveedores, "proveedor", "proveedores")}`;
}

export function filtrarPorLote(
  items: FacturaParaPagar[],
  filtro: "todos" | "sin_lote" | number
): FacturaParaPagar[] {
  if (filtro === "todos") return items;
  if (filtro === "sin_lote") return items.filter((f) => f.lote_id === null || f.lote_id === undefined);
  return items.filter((f) => f.lote_id === filtro);
}

/**
 * Filtro "Lote" combinado con el de vencimiento. Un lote puntual es un
 * conjunto explícito: se muestran TODAS sus facturas, sin importar el
 * vencimiento, para que el total en pantalla coincida con el del lote.
 * "Todos" y "Sin lote" aplican el vencimiento igual que siempre.
 */
export function filtrarPorLoteYVencimiento(
  items: FacturaParaPagar[],
  filtro: "todos" | "sin_lote" | number,
  aplicarVencimiento: (items: FacturaParaPagar[]) => FacturaParaPagar[]
): FacturaParaPagar[] {
  if (typeof filtro === "number") return filtrarPorLote(items, filtro);
  return filtrarPorLote(aplicarVencimiento(items), filtro);
}

// ---------------------------------------------------------------------------
// [NUEVO 04/11/2026, Tarea 9] Lógica de Control de pagos y del detalle del lote
// ---------------------------------------------------------------------------

/** El banco escribe UYU/USD/EUR; la bandeja y los lotes usan $/USD/EUR. */
export const MONEDA_BANCO_A_BANDEJA: Record<string, string> = { UYU: "$", USD: "USD", EUR: "EUR" };

export function monedaBancoABandeja(moneda: string): string {
  return MONEDA_BANCO_A_BANDEJA[moneda] ?? moneda;
}

/** Un lote pagado ya no retiene sus facturas ni admite cambios. */
export function esFinal(estado: EstadoLotePago): boolean {
  return estado === "PAGADO" || estado === "PAGADO_SIN_APROBACION";
}

export interface AccionesLote {
  /** Agregar, sacar o mover facturas. */
  editarFacturas: boolean;
  vincular: boolean;
  corregir: boolean;
  eliminar: boolean;
}

/**
 * Qué puede hacer Contaduría con un lote según su estado. Cualquier otro rol
 * (Control de pagos) solo lo ve. El backend sigue siendo la barrera real.
 */
export function accionesDeLote(l: Pick<LotePago, "estado" | "lote_banco_id">, esContaduria: boolean): AccionesLote {
  if (!esContaduria) return { editarFacturas: false, vincular: false, corregir: false, eliminar: false };
  const editable = esEditable(l.estado);
  return {
    editarFacturas: editable,
    vincular: editable && (l.lote_banco_id === null || l.lote_banco_id === undefined),
    corregir: l.estado === "CONCILIADO",
    // [AMPLIADO 04/11/2026] También un lote rechazado (PARA_CORREGIR), para que no quede trabado.
    eliminar: editable,
  };
}

/** PDFs del banco que se le pueden vincular a un lote: misma moneda y sin vínculo. */
export function lotesBancoParaVincular(lotesBanco: LoteBanco[], monedaLote: string): LoteBanco[] {
  return lotesBanco.filter((b) => monedaBancoABandeja(b.moneda) === monedaLote && !b.lote_pago);
}

const montoLote = (l: Pick<LotePago, "moneda" | "total">) => `${l.moneda} ${formatMonto(l.total)}`;

export function textoConfirmarEliminar(l: LotePago): string {
  return (
    `¿Eliminar ${l.nombre} (${montoLote(l)}, ${plural(l.cantidad_facturas, "factura", "facturas")})?\n\n` +
    "Las facturas quedan libres para armar otro lote. No toca SAP ni el banco."
  );
}

export function textoConfirmarCorregir(l: LotePago): string {
  const pdf = l.lote_banco_id_transaccion ? ` (ID ${l.lote_banco_id_transaccion})` : "";
  const n = l.aprobaciones.length;
  const perdidas =
    n > 0
      ? `\n\nSe ${n === 1 ? "pierde" : "pierden"} ${plural(n, "aprobación", "aprobaciones")} que ya tenía: ` +
        "habrá que volver a aprobarlo."
      : "";
  return (
    `¿Corregir ${l.nombre}?\n\n` +
    `Se desvincula del PDF del banco${pdf} y el lote vuelve a quedar abierto para agregar, sacar o mover ` +
    "facturas. Después lo volvés a vincular." +
    perdidas
  );
}

export function textoConfirmarVincular(l: LotePago, b: LoteBanco): string {
  return (
    `¿Vincular ${l.nombre} (${montoLote(l)}) con el PDF del banco del ${formatFecha(b.fecha)} ` +
    `(${b.moneda} ${formatMonto(b.importe_total)}, ID ${b.id_transaccion})?\n\n` +
    "Se concilia por proveedor en el momento. Si algo no cuadra, lo vas a ver en el detalle del lote."
  );
}

function descripcionFactura(f: FacturaDeLote, moneda: string): string {
  const comprobante = f.numero_comprobante ?? "sin número";
  const proveedor = f.proveedor_nombre ?? "proveedor sin nombre";
  return `${comprobante} de ${proveedor} (${moneda} ${formatMonto(f.saldo)})`;
}

export function textoConfirmarQuitarFactura(f: FacturaDeLote, l: LotePago): string {
  return `¿Sacar la factura ${descripcionFactura(f, l.moneda)} de ${nombreCorto(l.nombre)}?\n\nQueda en la bandeja, sin lote.`;
}

export function textoConfirmarMover(f: FacturaDeLote, origen: LotePago, destino: LotePago): string {
  return (
    `¿Mover la factura ${descripcionFactura(f, origen.moneda)} de ${nombreCorto(origen.nombre)} ` +
    `a ${nombreCorto(destino.nombre)}?`
  );
}

/** Aviso después de vincular, con lo que pasó en la conciliación. */
export function textoResultadoVinculo(nombre: string, r: { estado: EstadoLotePago; cierra: boolean | null }): string {
  if (esFinal(r.estado)) {
    return (
      `${nombre} quedó vinculado, pero el PDF ya estaba pagado en el banco: ` +
      `queda como «${ETIQUETA_ESTADO_LOTE[r.estado]}».`
    );
  }
  if (Boolean(r.cierra)) return `${nombre} vinculado: cierra con el banco ✓. Ya lo pueden aprobar.`;
  return `${nombre} vinculado, pero no cierra con el banco: abrí el detalle para ver qué proveedor no cuadra.`;
}

/** Fila del detalle: un proveedor del lote, con su conciliación si la hay. */
export interface FilaProveedorLote {
  clave: string;
  card_code: string | null;
  nombre: string;
  importe_app: number | null;
  importe_banco: number | null;
  diferencia: number | null;
  /** null = el lote todavía no se concilió. */
  resultado: ResultadoConciliacion | null;
  noCierra: boolean;
  conFacturaNoHabilitada: boolean;
  facturas: FacturaDeLote[];
}

function aNumero(v: string | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

const redondear = (n: number) => Math.round(n * 100) / 100;

/**
 * Una fila por proveedor: los que no cierran primero (en rojo en la pantalla),
 * después el resto, cada grupo por nombre. Entran también los proveedores que
 * solo aparecen del lado del banco, que no tienen facturas en el lote.
 */
export function filasDetalleLote(lote: LotePagoDetalle): FilaProveedorLote[] {
  const claveProveedor = (cardCode: string | null, nombre: string) => cardCode ?? "sin-codigo:" + nombre;
  const facturasPorClave = new Map<string, FacturaDeLote[]>();
  for (const f of lote.facturas) {
    const clave = claveProveedor(f.card_code, f.proveedor_nombre ?? "—");
    const lista = facturasPorClave.get(clave);
    if (lista) lista.push(f);
    else facturasPorClave.set(clave, [f]);
  }

  const filas: FilaProveedorLote[] = [];
  const usadas = new Set<string>();
  for (const c of lote.conciliacion) {
    const clave = claveProveedor(c.card_code, c.nombre);
    // Dos líneas del banco sin proveedor pueden repetir nombre: cada una es su fila.
    const repetida = usadas.has(clave);
    const facturas = repetida ? [] : facturasPorClave.get(clave) ?? [];
    usadas.add(clave);
    filas.push({
      clave: repetida ? `${clave}#${filas.length}` : clave,
      card_code: c.card_code,
      nombre: c.nombre,
      importe_app: aNumero(c.importe_app),
      importe_banco: aNumero(c.importe_banco),
      diferencia: aNumero(c.diferencia),
      resultado: c.resultado,
      noCierra: c.resultado !== "OK",
      conFacturaNoHabilitada: facturas.some((f) => !Boolean(f.habilitada)),
      facturas,
    });
  }
  for (const [clave, facturas] of facturasPorClave) {
    if (usadas.has(clave)) continue;
    filas.push({
      clave,
      card_code: facturas[0].card_code,
      nombre: facturas[0].proveedor_nombre ?? "—",
      importe_app: redondear(facturas.reduce((s, f) => s + (aNumero(f.saldo) ?? 0), 0)),
      importe_banco: null,
      diferencia: null,
      resultado: null,
      noCierra: false,
      conFacturaNoHabilitada: facturas.some((f) => !Boolean(f.habilitada)),
      facturas,
    });
  }
  return filas.sort((a, b) =>
    a.noCierra !== b.noCierra ? (a.noCierra ? -1 : 1) : a.nombre.localeCompare(b.nombre, "es")
  );
}

/** Parte local del UPN ("ana.perez@pontyn.com.uy" → "ana.perez"), igual que `textoAprobaciones`. */
function usuarioLocal(upn: string | null | undefined): string {
  return upn ? upn.split("@")[0] : "—";
}

/** Datos de una factura en el detalle del lote; los que faltan, "—". */
export function datosFacturaDeLote(
  f: Pick<FacturaDeLote, "nro_documento" | "fecha_vencimiento" | "habilitado_por">
): { documento: string; vencimiento: string; habilitadoPor: string } {
  return {
    documento: f.nro_documento || "—",
    vencimiento: formatFecha(f.fecha_vencimiento),
    habilitadoPor: usuarioLocal(f.habilitado_por),
  };
}

const ETIQUETA_ESTADO_BANCO: Record<string, string> = { completada: "Completada", procesando: "Procesando" };

/** Encabezado del detalle: "TX-9 · 03/11/2026 · Completada", o "sin vincular". */
export function textoBancoVinculado(
  l: Pick<LotePago, "lote_banco_id_transaccion" | "lote_banco_fecha" | "lote_banco_estado">
): string {
  if (!l.lote_banco_id_transaccion) return "sin vincular";
  const partes = [l.lote_banco_id_transaccion];
  if (l.lote_banco_fecha) partes.push(formatFecha(l.lote_banco_fecha));
  if (l.lote_banco_estado) partes.push(ETIQUETA_ESTADO_BANCO[l.lote_banco_estado] ?? l.lote_banco_estado);
  return partes.join(" · ");
}

/** Cantidad de proveedores que no dan OK en la conciliación vigente. */
export function noCierranDe(lote: Pick<LotePagoDetalle, "conciliacion">): number {
  return lote.conciliacion.filter((c) => c.resultado !== "OK").length;
}

/** El detalle trae las aprobaciones de todas las versiones: cuentan solo las vigentes. */
export function aprobacionesVigentes(
  lote: Pick<LotePagoDetalle, "version" | "aprobaciones">
): AprobacionLoteDetalle[] {
  return lote.aprobaciones.filter((a) => a.version === lote.version && a.decision === "APROBADO");
}

function detalleDeEvento(e: EventoLote): Record<string, unknown> {
  if (!e.detalle) return {};
  try {
    const d: unknown = JSON.parse(e.detalle);
    return d && typeof d === "object" ? (d as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** Una línea del historial del lote, en lenguaje del usuario. */
export function textoEvento(e: EventoLote): string {
  const d = detalleDeEvento(e);
  const cantidad = typeof d.cantidad === "number" ? d.cantidad : null;
  const facturas = cantidad === null ? "facturas" : plural(cantidad, "factura", "facturas");
  const texto = (clave: string): string | null => {
    const v = d[clave];
    return typeof v === "string" && v ? v : null;
  };
  switch (e.tipo) {
    case "creado":
      return cantidad === null ? "Armó el lote" : `Armó el lote con ${facturas}`;
    case "factura_agregada":
      return `Agregó ${facturas}`;
    case "factura_quitada":
      return `Sacó ${facturas}`;
    case "factura_movida":
      return d.origen_id !== undefined ? `Recibió ${facturas} de otro lote` : `Movió ${facturas} a otro lote`;
    case "eliminado":
      return "Eliminó el lote";
    case "vinculado":
      return "Vinculó el PDF del banco";
    case "conciliado":
      return texto("detalle") ? `Concilió: ${texto("detalle")}` : "Concilió con el banco";
    case "desvinculado":
      return "Desvinculó el PDF del banco";
    case "aprobado":
      return texto("comentario") ? `Aprobó: «${texto("comentario")}»` : "Aprobó";
    case "rechazado":
      return texto("motivo") ? `Rechazó: «${texto("motivo")}»` : "Rechazó";
    case "pagado":
      return "El banco lo pagó";
    case "pagado_sin_aprobacion":
      return "El banco lo pagó sin aprobación en la app";
    default:
      return e.tipo;
  }
}

/** Opciones del filtro "Lote": todos, sin lote y cada lote activo. */
export function opcionesFiltroLote(lotes: LotePago[]): Array<{ valor: string; etiqueta: string }> {
  return [
    { valor: "todos", etiqueta: "Todos" },
    { valor: "sin_lote", etiqueta: "Sin lote" },
    ...lotes.filter((l) => !esFinal(l.estado)).map((l) => ({ valor: String(l.id), etiqueta: l.nombre })),
  ];
}

/**
 * Del valor del select al filtro de `filtrarPorLote`. Un lote que ya no está
 * activo (se eliminó o se pagó) vuelve a "todos", para no dejar la tabla vacía
 * sin explicación.
 */
export function filtroLoteDeValor(valor: string, lotes: LotePago[]): "todos" | "sin_lote" | number {
  if (valor === "sin_lote") return "sin_lote";
  const id = Number(valor);
  return lotes.some((l) => l.id === id && !esFinal(l.estado)) ? id : "todos";
}

export interface CandidatoVinculo {
  id: number;
  nombre: string;
  total: string;
}

export interface VinculoImportacion {
  loteBancoId: number;
  /** El PDF ya estaba vinculado a este lote de la app. */
  vinculado: { id: number; nombre: string; estado: EstadoLotePago } | null;
  /** Único lote que coincide dentro de la tolerancia, si lo hay. */
  sugerido: CandidatoVinculo | null;
  candidatos: CandidatoVinculo[];
}

/**
 * Lo que la importación de un PDF trae para vincularlo. `null` con la
 * funcionalidad apagada: la respuesta no trae ninguno de los campos nuevos.
 */
export function vinculoDeImportacion(r: {
  lote_id?: number;
  lote_pago?: { id: number; nombre: string; estado: EstadoLotePago } | null;
  sugerido?: number | null;
  candidatos?: CandidatoVinculo[];
}): VinculoImportacion | null {
  if (r.lote_id === undefined || r.lote_id === null) return null;
  if (r.lote_pago === undefined && r.sugerido === undefined && r.candidatos === undefined) return null;
  const candidatos = r.lote_pago ? [] : r.candidatos ?? [];
  return {
    loteBancoId: r.lote_id,
    vinculado: r.lote_pago ?? null,
    sugerido: candidatos.find((c) => c.id === r.sugerido) ?? null,
    candidatos,
  };
}

export function textoSugerenciaVinculo(c: CandidatoVinculo, moneda?: string): string {
  const monto = moneda ? `${moneda} ${formatMonto(c.total)}` : formatMonto(c.total);
  return `Vincular a ${nombreCorto(c.nombre)} (${monto}) — coincide ✓`;
}

/**
 * Mensaje de error de una acción sobre un lote. Los 400/404/409 del backend
 * ya vienen en español y en lenguaje del usuario ("Solo se puede eliminar un
 * lote abierto"): se muestran tal cual. `status` null = error de red.
 */
export function mensajeErrorLote(status: number | null, texto: string, descripcion: string): string {
  if (status === null) {
    return (
      `Error de conexión al ${descripcion}. La acción PUEDE haberse aplicado igual: ` +
      "la pantalla ya se actualizó, revisá antes de reintentar."
    );
  }
  if (status === 403) return `No tenés permiso para ${descripcion}.`;
  if ([400, 404, 409].includes(status)) {
    const t = texto.trim();
    if (t && t.length <= 300 && !/[<>]/.test(t)) return t;
    return `No se pudo ${descripcion} (error ${status}).`;
  }
  if (status >= 500) {
    return (
      `El servidor tardó demasiado o falló al ${descripcion}. Ojo: la acción PUEDE haberse aplicado igual — ` +
      "la pantalla ya se actualizó, revisá antes de reintentar."
    );
  }
  return `No se pudo ${descripcion} (error ${status}).`;
}

/** Aviso después de crear un lote. */
export function textoLoteCreado(nombre: string, sel: FacturaParaPagar[]): string {
  const totales = totalesPorMoneda(sel.map((f) => ({ monto: f.saldo_vencido, moneda: f.moneda })))
    .map(({ moneda, total }) => `${moneda} ${formatMonto(total)}`)
    .join(" · ");
  return `Se armó ${nombre} con ${plural(sel.length, "factura", "facturas")}${totales ? ` (${totales})` : ""}.`;
}

// ---------------------------------------------------------------------------
// [NUEVO 04/11/2026, Tarea 10] Página "Aprobación de pagos"
// ---------------------------------------------------------------------------

export type TabAprobacionPagos = "para_aprobar" | "aprobados" | "para_corregir" | "pagados";

export const TABS_APROBACION_PAGOS: Array<{
  clave: TabAprobacionPagos;
  etiqueta: string;
  estados: EstadoLotePago[];
  /** Historial: se filtra por día de creación del lote. */
  conFechas: boolean;
}> = [
  { clave: "para_aprobar", etiqueta: "Para aprobar", estados: ["CONCILIADO"], conFechas: false },
  { clave: "aprobados", etiqueta: "Aprobados", estados: ["APROBADO"], conFechas: false },
  { clave: "para_corregir", etiqueta: "Rechazados / para corregir", estados: ["PARA_CORREGIR"], conFechas: false },
  { clave: "pagados", etiqueta: "Pagados", estados: ["PAGADO", "PAGADO_SIN_APROBACION"], conFechas: true },
];

/** El `estado` de GET /lotes-pago para esa pestaña (lista separada por comas). */
export function estadosDeTab(tab: TabAprobacionPagos): string {
  return (TABS_APROBACION_PAGOS.find((t) => t.clave === tab)?.estados ?? []).join(",");
}

/**
 * Los lotes que se muestran en la pestaña. En "Para aprobar" quedan solo los
 * que cierran: el backend ya oculta los que no cierran a quien es solo
 * aprobador, pero Contaduría los recibe igual y no corresponde mostrárselos
 * como "para aprobar" (spec §3: un lote que no cierra no llega a aprobación).
 */
export function lotesDeTab<L extends Pick<LotePago, "estado" | "cierra">>(tab: TabAprobacionPagos, lotes: L[]): L[] {
  const estados = TABS_APROBACION_PAGOS.find((t) => t.clave === tab)?.estados ?? [];
  return lotes.filter((l) => estados.includes(l.estado) && (tab !== "para_aprobar" || Boolean(l.cierra)));
}

const normalizarUsuario = (u: string | null | undefined) => (u ?? "").trim().toLowerCase();

/**
 * Badge del menú: los lotes de "Para aprobar" que `usuario` PUEDE aprobar
 * (sin los que armó ni los que ya aprobó), con la misma regla que decide el
 * botón (`decisionAprobacion`). En el listado, `aprobaciones` ya son las de
 * la versión vigente. Sin usuario, cuenta todos los de la pestaña.
 */
export function cantidadParaAprobar(
  lotes: Array<Pick<LotePago, "estado" | "cierra" | "creado_por" | "aprobaciones">>,
  usuario?: string
): number {
  return lotesDeTab("para_aprobar", lotes).filter(
    (l) => decisionAprobacion(l, l.aprobaciones, usuario ?? "", true).puedeAprobar
  ).length;
}

export interface DecisionAprobacion {
  puedeAprobar: boolean;
  puedeRechazar: boolean;
  /** Por qué no se puede aprobar, cuando sí se podría rechazar. */
  motivoNoAprobar: string | null;
}

/**
 * Qué botones ve quien mira el lote. Refleja `decidir_aprobacion` del backend
 * para no ofrecer un botón que va a dar error; el backend sigue siendo la
 * barrera real. `vigentes` = aprobaciones de la versión vigente.
 */
export function decisionAprobacion(
  lote: Pick<LotePago, "estado" | "cierra" | "creado_por">,
  vigentes: AprobacionLote[],
  usuario: string,
  esAprobador: boolean
): DecisionAprobacion {
  if (!esAprobador || lote.estado !== "CONCILIADO") {
    return { puedeAprobar: false, puedeRechazar: false, motivoNoAprobar: null };
  }
  const yo = normalizarUsuario(usuario);
  let motivoNoAprobar: string | null = null;
  if (!Boolean(lote.cierra)) motivoNoAprobar = "Este lote no cierra con el banco: solo se puede rechazar.";
  else if (yo && normalizarUsuario(lote.creado_por) === yo) {
    motivoNoAprobar = "Este lote lo armaste vos: lo tienen que aprobar otras personas.";
  } else if (yo && vigentes.some((a) => normalizarUsuario(a.usuario) === yo)) {
    motivoNoAprobar = "Ya lo aprobaste. Falta la aprobación de otra persona.";
  }
  return { puedeAprobar: motivoNoAprobar === null, puedeRechazar: true, motivoNoAprobar };
}

/**
 * Lista después de aprobar, antes de que responda el backend. Con la
 * aprobación que completa las necesarias el lote sale de "Para aprobar"; si
 * no, se queda con la aprobación sumada ("1 de 2"). No muta: el llamador
 * guarda la lista anterior para volver atrás si la llamada falla.
 */
export function aprobacionOptimista<L extends Pick<LotePago, "id" | "aprobaciones">>(
  lotes: L[],
  id: number,
  usuario: string,
  fecha: string
): L[] {
  const l = lotes.find((x) => x.id === id);
  if (!l) return lotes;
  if (l.aprobaciones.length + 1 >= APROBACIONES_NECESARIAS) return sinLote(lotes, id);
  return lotes.map((x) => (x.id === id ? { ...x, aprobaciones: [...x.aprobaciones, { usuario, fecha }] } : x));
}

/** Lista sin ese lote (rechazo optimista). */
export function sinLote<L extends Pick<LotePago, "id">>(lotes: L[], id: number): L[] {
  return lotes.filter((l) => l.id !== id);
}

/** Aviso después de aprobar, con lo que respondió el backend. */
export function textoResultadoAprobacion(
  nombre: string,
  r: { estado: EstadoLotePago; aprobaciones: number }
): string {
  const corto = nombreCorto(nombre);
  if (r.estado === "APROBADO") {
    return `${corto} quedó aprobado (${r.aprobaciones} de ${APROBACIONES_NECESARIAS}). Ya se puede firmar en el banco.`;
  }
  return `Registraste tu aprobación de ${corto} (${r.aprobaciones} de ${APROBACIONES_NECESARIAS}). Falta la de otra persona.`;
}

/**
 * Como `mensajeErrorLote`, pero un 403 de aprobar/rechazar trae un motivo
 * concreto ("No podés aprobar un lote que armaste vos") y se muestra tal cual.
 * El 403 genérico de rol sigue con el mensaje de permiso de siempre.
 */
export function mensajeErrorDecision(status: number | null, texto: string, descripcion: string): string {
  if (status === 403 && !/rol requerido/i.test(texto)) {
    const t = texto.trim();
    if (t && t.length <= 300 && !/[<>]/.test(t)) return t;
  }
  return mensajeErrorLote(status, texto, descripcion);
}

/**
 * Evento de ventana que avisa al menú que cambió un lote, para refrescar el
 * badge de "Aprobación de pagos" sin un contexto nuevo.
 */
export const EVENTO_LOTES_PAGO_CAMBIARON = "lotes-pago-cambiaron";
